// Centralized broker & contact configuration
// Update affiliate links here if they change.

import { useEffect, useState } from 'react';

const GEO_CACHE_KEY = 'nikayofx_geo';
const GEO_TTL_MS = 24 * 60 * 60 * 1000;
let _countryCode = null;

try {
    const cached = JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || 'null');
    if (cached && cached.cc && Date.now() - cached.at < GEO_TTL_MS) _countryCode = cached.cc;
} catch { /* ignore */ }

// Test override: visit any page with ?geo=NG (or SA, EG, JO...) to force a country.
// Persists for the session so events fire with that region. Remove with ?geo=off
try {
    const forced = new URLSearchParams(window.location.search).get('geo');
    if (forced) {
        if (forced.toLowerCase() === 'off') sessionStorage.removeItem('nikayofx_geo_force');
        else sessionStorage.setItem('nikayofx_geo_force', forced.toUpperCase());
    }
    const forcedCC = sessionStorage.getItem('nikayofx_geo_force');
    if (forcedCC) _countryCode = forcedCC;
} catch { /* ignore */ }

const _tzFallbackIsNigeria = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const lang = (navigator.language || '').toLowerCase();
        return tz === 'Africa/Lagos' || lang.endsWith('-ng');
    } catch {
        return false;
    }
};

const _saveGeo = (cc) => {
    _countryCode = cc;
    try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ cc, at: Date.now() })); } catch { /* ignore */ }
    window.dispatchEvent(new Event('nikayofx:geo'));
};

if (typeof window !== 'undefined' && !_countryCode) {
    fetch('https://ipapi.co/json/')
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => { if (d?.country_code) _saveGeo(d.country_code); else throw new Error(); })
        .catch(() =>
            fetch('https://api.country.is')
                .then((r) => (r.ok ? r.json() : Promise.reject()))
                .then((d) => { if (d?.country) _saveGeo(d.country); })
                .catch(() => { /* stay on tz fallback */ })
        );
}

export const isNigeria = () => (_countryCode ? _countryCode === 'NG' : _tzFallbackIsNigeria());

export const getGeoCountry = () => {
    if (_countryCode) return _countryCode;
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (tz === 'Africa/Lagos') return 'NG';
        if (tz === 'Asia/Riyadh') return 'SA';
        if (tz === 'Africa/Cairo') return 'EG';
    } catch { /* ignore */ }
    return null;
};

const HIGH_DEPOSIT_COUNTRIES = ['SA', 'AE', 'KW', 'QA', 'BH', 'OM', 'IQ'];
export const isHighDepositCountry = () => HIGH_DEPOSIT_COUNTRIES.includes(getGeoCountry());

// Paid training-fee options ($30 one-time educational training fee)
export const TRAINING_FEE_USD = 30;
export const PAYMENTS = {
    // Local-currency Flutterwave links per country; anyone else gets the USD link.
    flutterwaveByCountry: {
        NG: { url: 'https://flutterwave.com/pay/iwm2jx7fg92m', qr: '/pay/flutterwave-ngn.png', method: 'flutterwave_ngn', label: 'Pay in Naira (₦40,000)', currency: 'NGN' },
        GH: { url: 'https://flutterwave.com/pay/bnddih16vn68', qr: '/pay/flutterwave-gh.png', method: 'flutterwave_ghs', label: 'Pay in Ghana Cedis (GHS)', currency: 'GHS' },
        ZA: { url: 'https://flutterwave.com/pay/oadtchyx2pvg', qr: '/pay/flutterwave-za.png', method: 'flutterwave_zar', label: 'Pay in South African Rand (ZAR)', currency: 'ZAR' },
        CM: { url: 'https://flutterwave.com/pay/dwyz7sebzoxq', qr: '/pay/flutterwave-cm.png', method: 'flutterwave_xaf', label: 'Pay in CFA Francs (XAF)', currency: 'XAF' },
        KE: { url: 'https://flutterwave.com/pay/hdw9chsj9sk3', qr: '/pay/flutterwave-ke.png', method: 'flutterwave_kes', label: 'Pay in Kenyan Shillings (KES)', currency: 'KES' },
    },
    flutterwaveDefault: { url: 'https://flutterwave.com/pay/ixhym1mea2yl', qr: '/pay/flutterwave-usd.png', method: 'flutterwave_usd', label: 'Pay in USD ($30)', currency: 'USD' },
    crypto: [
        { id: 'usdt_trc20', coin: 'USDT', network: 'TRC20 (Tron)', address: 'TN1yS1M83crnKneFLvN2QB6Eo3J1tcmvnv', qr: '/pay/usdt-trc20.jpg' },
        { id: 'usdt_erc20', coin: 'USDT', network: 'ERC20 (Ethereum)', address: '0x422ddb76850d20ce299419b8cbf5878a2316530c', qr: '/pay/usdt-erc20.jpg' },
    ],
};
export const getFlutterwaveLink = () => PAYMENTS.flutterwaveByCountry[getGeoCountry()] || PAYMENTS.flutterwaveDefault;

export const getMinDeposit = () => 150;

// Re-renders when the IP geo lookup resolves
export const useMinDeposit = () => {
    const [min, setMin] = useState(getMinDeposit());
    useEffect(() => {
        const h = () => setMin(getMinDeposit());
        window.addEventListener('nikayofx:geo', h);
        return () => window.removeEventListener('nikayofx:geo', h);
    }, []);
    return min;
};

export const BROKERS = {
    exness: {
        id: 'exness',
        name: 'Exness',
        get minDeposit() { return getMinDeposit(); },
        currency: 'USD',
        affiliateUrl: 'https://one.exnessonelink.com/a/c_g161r7qhke/?campaign=42861',
        ibUrl: 'https://one.exnessonelink.com/a/k4s802pi6j',
        accent: '#FFB800',
        tagline: 'Trusted Global Broker',
        perks: ['Instant withdrawals', 'Trusted global broker', 'Ultra-tight spreads'],
    },
};

export const DEFAULT_BROKER = BROKERS.exness;

export const getFreeTelegramUrl = (lang) =>
    (lang || '').startsWith('ar') ? CONTACTS.freeTelegram.arabicUrl : CONTACTS.freeTelegram.url;

export const getWhatsappGroupUrl = (lang) =>
    (lang || '').startsWith('ar') ? CONTACTS.whatsappGroup.arabicUrl : CONTACTS.whatsappGroup.url;

// Brand motto
export const MOTTO = 'GOD IS OUR BACKING';

export const CONTACTS = {
    whatsapp: {
        number: '+2348081155255',
        url: 'https://wa.me/2348081155255',
    },
    telegram: {
        handle: '@Nikayofx',
        url: 'https://t.me/Nikayofx',
    },
    freeTelegram: {
        label: 'Join Our Free Telegram',
        url: 'https://t.me/+2NjlhqqR_7c5NTBk',
        arabicUrl: 'https://t.me/+-82nVtkR-LZkMjc0',
    },
    whatsappGroup: {
        label: 'Join Our Free WhatsApp Group',
        url: 'https://chat.whatsapp.com/CimrDPqvLVlLsyELTbUJX0',
        arabicUrl: 'https://chat.whatsapp.com/BmLS0pZ0DE03CcpVKIQaqw',
    },
    whatsappCta: {
        label: 'Chat with Nikayo',
        url: `https://wa.me/2348081155255?text=${encodeURIComponent("Hi Nikayofx 👋 I'd like to join your Private Trade Guidance — can I know more?")}`,
    },
    socials: {
        facebook: 'https://www.facebook.com/profile.php?id=61587337710518',
        youtube: 'https://www.youtube.com/@Nikayofx',
        tiktok: 'https://www.tiktok.com/@Nikayofx',
        x: 'https://x.com/NIKAYO_FX',
    },
};

// External brand sites
export const SISTER_SITES = {
    goldSignalLab: {
        name: 'Gold Signal Lab',
        url: 'https://nikayofx.co.uk',
        tagline: 'Gold-only signals — XAUUSD specialists',
    },
};
